<!--
  PaymentsPage -- Distributor App > Account > Payments.

  PR-4 of the distributor-payments redesign. Rebuilds the page around PR-3's
  distributor-billing endpoints (consumed via billingService), replacing the
  legacy per-seat subscription panel + useTeamSubscription read path.

  Composition (design's three sections, billing-only -- recent orders moved to
  Reporting > Orders):
    - StripeConnectCard    (GET /distributor/billing/stripe-connect)
    - ThreadFlowPlanCard   (GET /distributor/billing/plan + estimated-invoice)
    - StoreBillingCard     (GET /distributor/billing/stores -- loads its own data)
    - EditPaymentInfoDialog (Stripe Elements card update -- in scope, #733)
    - CancelSubscriptionDialog (Danger zone -- re-wired onto the new state
      machine in PR-5; kept here so the affordance survives the rebuild)

  Tenant resolution: the billing endpoints resolve the tenant SERVER-SIDE via
  effective_distributor_id() (impersonation rides on the axios interceptor
  app-wide). The page passes no distributor id. When useAccountStore().distributor
  is null (a true unscoped Super Admin), the endpoints return a null payload and
  the page renders an "impersonate a distributor" empty state instead of erroring.

  NOTE: the legacy `ln` subscription table / read path drop is a SEPARATE
  migration PR (PR-4b). This PR only stops the PAGE from reading it.
-->
<template>
  <v-container fluid data-testid="dist-account-payments-page">
    <v-row>
      <v-col cols="12">
        <h1 class="md-header">Payments</h1>
        <p class="md-lede">
          Your Stripe connection, your ThreadFlow billing, and where every
          dollar goes.
        </p>

        <v-progress-circular
          v-if="loading"
          :indeterminate="true"
          color="primary"
          size="32"
          width="4"
          class="mt-4"
          data-testid="payments-loading"
        />

        <!-- A billing GET actually failed (network / 5xx). Distinct from the
             unscoped empty state below so we don't mislead the user. -->
        <v-card
          v-else-if="loadError"
          class="mt-4 tf-empty-state"
          data-testid="payments-load-error"
        >
          <v-card-text>
            We couldn't load your billing details. Please refresh to try
            again.
          </v-card-text>
        </v-card>

        <!-- Unscoped Super Admin (scope === "unscoped"): no tenant to scope.
             This is the ONLY case that gets the "impersonate" empty state. A
             validly-scoped distributor with no subscription does NOT land
             here -- it falls through to the no-subscription branch below. -->
        <v-card
          v-else-if="isUnscoped"
          class="mt-4 tf-empty-state"
          data-testid="payments-empty-state"
        >
          <v-card-text>
            Impersonate a distributor to view billing.
          </v-card-text>
        </v-card>

        <template v-else>
          <!-- Stripe Connect is independent of the ThreadFlow subscription:
               a distributor with no active plan can (and should) still see /
               manage their Stripe connection. Rendered in BOTH the
               with-subscription and no-subscription states. -->
          <StripeConnectCard
            v-if="stripeConnect"
            :connect="stripeConnect"
            :oauth-result="oauthResult"
            class="mt-4"
            @connected="onConnectCompleted"
          />

          <!-- Read-only: where ThreadFlow invoices go. The billing contact is
               edited on Company (IA unit U1, D1), never here. -->
          <p
            class="mt-6 mb-0"
            data-testid="payments-invoices-go-to"
          >
            Invoices go to: {{ invoicesGoTo }}.
            <router-link
              to="/distributor/account/company"
              data-testid="payments-invoices-go-to-edit"
            >Edit on Company</router-link>
          </p>

          <!-- ThreadFlow BILLING (ACH Direct Debit) -- how WE invoice THEM.
               Deliberately adjacent to the Connect card above so payments
               setup reads as one session, but a DIFFERENT Stripe
               relationship: Connect is how the distributor gets paid by
               shoppers, this is how ThreadFlow collects the 1st/15th
               invoices. Rendered for any scoped distributor, including one
               with no plan yet -- authorizing ACH early is harmless and
               avoids a second administrative step later. -->
          <AchBillingCard
            :ach="achStatus"
            :verification-pending="achVerificationPending"
            class="mt-6"
            @setup-started="onAchSetupStarted"
            @refresh-status="refreshAchStatus()"
          />

          <!-- Scoped distributor with NO subscription yet: show a clear "no
               active plan" card instead of the full plan card, and hide the
               danger zone (nothing to cancel). -->
          <v-card
            v-if="hasNoSubscription"
            class="mt-6 tf-empty-state"
            data-testid="payments-no-subscription"
          >
            <v-card-title><h2>No active ThreadFlow plan</h2></v-card-title>
            <v-card-text>
              This distributor doesn't have an active ThreadFlow plan yet.
              Once a plan is set up, billing details and charges will appear
              here. You can still connect Stripe above.
            </v-card-text>
          </v-card>

          <ThreadFlowPlanCard
            v-else-if="plan"
            :plan="plan"
            :invoice="estimatedInvoice"
            class="mt-6"
            @update-card="openEditPaymentDialog"
          />

          <!-- Store billing: each store's class, reason and amount for a
               month (current month to date, or a closed month's frozen
               record). Loads its own data, so a failure here is contained
               to this card. Shown only alongside a plan. -->
          <StoreBillingCard v-if="plan && !hasNoSubscription" class="mt-6" />

          <!-- Danger zone -- re-wired onto distributor_subscription in PR-5.
               Hidden when there is no subscription (nothing to cancel). -->
          <v-card
            v-if="!hasNoSubscription"
            class="mt-8 danger-zone"
            data-testid="payments-danger-zone"
          >
            <v-card-title>
              <h2>Danger zone</h2>
              <HelpIcon
                audience="owner"
                slug="cancel-or-renew-your-subscription"
                heading="cancel-your-subscription"
              />
            </v-card-title>
            <v-card-text>
              Canceling stops billing and closes your storefronts. Your
              account is then read-only for 90 days: your team can still sign
              in and view stores, orders, batches and reports, but no one can
              make changes. To undo it, renew from the Stores list within
              those 90 days. After that the account is closed and no one can
              sign in; its data is kept, not erased.
            </v-card-text>
            <v-card-actions>
              <button
                type="button"
                class="tf-btn tf-btn--danger"
                data-testid="payments-cancel-account-btn"
                @click="openCancelDialog"
              >
                Cancel ThreadFlow
              </button>
            </v-card-actions>
          </v-card>
        </template>
      </v-col>
    </v-row>

    <EditPaymentInfoDialog
      ref="editPaymentDialog"
      :subscription="editDialogSubscription"
      @update-payment-method="onPaymentMethodUpdated"
    />

    <AchBankDialog
      ref="achBankDialog"
      :stripe-publishable-key="achPublishableKey"
      @completed="onAchCompleted"
      @pending="onAchPending"
    />

    <CancelSubscriptionDialog
      ref="cancelDialog"
      @cancel-subscription="onCancelSubscription"
    />
  </v-container>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import StripeConnectCard from "./StripeConnectCard.vue";
import ThreadFlowPlanCard from "./ThreadFlowPlanCard.vue";
import EditPaymentInfoDialog from "./EditPaymentInfoDialog.vue";
import CancelSubscriptionDialog from "./CancelSubscriptionDialog.vue";
import AchBillingCard from "./AchBillingCard.vue";
import AchBankDialog from "./AchBankDialog.vue";
import StoreBillingCard from "./StoreBillingCard.vue";
import {
  getBillingPlan,
  getStripeConnect,
  getEstimatedInvoice,
  getAchStatus,
  cancelSubscription,
  refreshStripeConnectStatus,
  completeStripeConnectOAuth,
} from "@/services/billingService";
import { getDistributorById } from "@/services/distributorService";
import { useAccountStore } from "@/stores/account";
import HelpIcon from "@/components/Help/HelpIcon.vue";

export default {
  name: "PaymentsPage",

  components: {
    HelpIcon,
    StripeConnectCard,
    ThreadFlowPlanCard,
    EditPaymentInfoDialog,
    CancelSubscriptionDialog,
    AchBillingCard,
    AchBankDialog,
    StoreBillingCard,
  },

  setup() {
    const plan = ref(null);
    const stripeConnect = ref(null);
    // The OAuth callback outcome, handed to StripeConnectCard so it can show
    // the never-silent confirmation when Stripe returned an account the
    // distributor did not ask for. Null on an ordinary page load.
    const oauthResult = ref(null);
    const estimatedInvoice = ref(null);
    // GET /distributor/billing/ach -- the ACH mandate status.
    const achStatus = ref(null);
    // The bank dialog finished with verification still running. Cleared when
    // the server reports a mandate (or on a fresh page load).
    const achVerificationPending = ref(false);
    const loading = ref(true);
    // Distinct from the unscoped empty state: set only when a billing GET
    // actually rejected (network / 5xx), so the page shows an error instead
    // of the misleading "impersonate a distributor" empty state.
    const loadError = ref(false);
    // Billing contact ({name, email}) for the read-only "Invoices go to"
    // line. Null until read, or when the distributor record is unreadable.
    const billingContact = ref(null);

    // distributor id is for DISPLAY / dialog props only -- billing endpoints
    // resolve the tenant server-side. Null for a true unscoped Super Admin.
    let distributorIdValue = null;
    try {
      const account = useAccountStore();
      distributorIdValue = account.distributor ? account.distributor.id : null;
    } catch (e) {
      distributorIdValue = null;
    }

    // PR-3 stamps a top-level `scope` discriminator on every plan payload:
    //   - "unscoped"    -> a true unscoped Super Admin (no tenant). ONLY this
    //                      case shows the "Impersonate a distributor" empty
    //                      state.
    //   - "distributor" -> a real tenant resolved (real Owner or SA
    //                      impersonating one), with OR without a subscription.
    //
    // Defensive fallback for an older/cached payload missing `scope`: if there
    // is distributor context present (a stripeConnect payload OR a non-null
    // tier), treat it as a scoped distributor; only a payload with no scope AND
    // no distributor signal falls back to the unscoped empty state.
    const isUnscoped = computed(() => {
      const p = plan.value;
      if (!p) return true;
      if (p.scope === "unscoped") return true;
      if (p.scope === "distributor") return false;
      // No explicit scope (defensive): a present tier or any stripeConnect
      // payload means a real tenant -- not the unscoped empty state.
      if (p.tier != null) return false;
      if (stripeConnect.value) return false;
      return true;
    });

    // A scoped distributor (NOT unscoped) that simply has no subscription yet:
    // null subscription/tier. Drives the "no active plan" card + hides the
    // danger zone, while the Stripe Connect card still renders.
    const hasNoSubscription = computed(() => {
      if (isUnscoped.value) return false;
      const p = plan.value;
      if (!p) return false;
      return p.subscription == null && p.tier == null;
    });

    // EditPaymentInfoDialog reads payment_info.{four_digits,...} off its
    // `subscription` prop for its summary line, and `stripe_publishable_key`
    // to tokenize the card on the distributor's BILLING platform. Its POST
    // (/distributor/billing/payment-method) sends only the new card; the
    // server resolves the Customer and the old card itself. The ids below
    // are kept for display/diagnostics only -- nothing trusts them.
    const editDialogSubscription = computed(() => {
      const p = plan.value;
      const pm = p && p.payment_method;
      // customer_id: display/diagnostics only. The top-level value may be a
      // placeholder; the server provisions the real billing Customer when a
      // first card is saved, so nothing here gates "Add a card".
      const customerId =
        (pm && pm.customer_id) || (p && p.stripe_customer_id) || null;
      const paymentMethodId = (pm && pm.payment_method_id) || null;
      if (!pm) {
        // No card on file yet: no payment_info summary.
        return {
          customer_id: customerId,
          payment_method_id: paymentMethodId,
          stripe_publishable_key: (p && p.stripe_publishable_key) || null,
        };
      }
      return {
        payment_info: {
          four_digits: pm.last4,
          exp_month: pm.exp_month,
          exp_year: pm.exp_year,
          brand: pm.brand,
        },
        customer_id: customerId,
        payment_method_id: paymentMethodId,
        stripe_publishable_key: (p && p.stripe_publishable_key) || null,
      };
    });

    // "Pat Lee, billing@acme.com", the email alone, or "not set". Read from
    // the distributor record Company edits; never blocks or fails the page.
    const invoicesGoTo = computed(() => {
      const c = billingContact.value;
      if (!c || !c.email) return "not set";
      return c.name ? `${c.name}, ${c.email}` : c.email;
    });

    async function loadBillingContact() {
      billingContact.value = null;
      if (distributorIdValue == null) return;
      try {
        const res = await getDistributorById(distributorIdValue);
        const attrs =
          (res && res.data && res.data.data && res.data.data.attributes) || {};
        billingContact.value = {
          name: attrs.billing_contact_name || null,
          email: attrs.email || null,
        };
      } catch (e) {
        billingContact.value = null;
      }
    }

    async function load() {
      loading.value = true;
      loadError.value = false;
      try {
        const [planRes, connectRes, invoiceRes] = await Promise.all([
          getBillingPlan(),
          getStripeConnect(),
          getEstimatedInvoice(),
        ]);
        plan.value = planRes ? planRes.data : null;
        stripeConnect.value = connectRes ? connectRes.data : null;
        estimatedInvoice.value = invoiceRes ? invoiceRes.data : null;

        // ACH status is fetched SEPARATELY and its failure is swallowed on
        // purpose. It is not in the Promise.all above because a distributor
        // on an older sandbox (or any transient 5xx from this one endpoint)
        // would otherwise take the whole page to payments-load-error --
        // hiding the plan and Connect cards over a card that is allowed to
        // be empty. Null simply renders the "Not set up" state.
        try {
          const achRes = await getAchStatus();
          applyAchStatus(achRes ? achRes.data : null, { notSetUpClears: true });
        } catch (e) {
          achStatus.value = null;
        }
        // Not awaited: an auxiliary read must never hold the page in its
        // loading state. It handles its own failure.
        loadBillingContact();
      } catch (e) {
        // A real fetch failure (one of the three GETs rejected). Surface a
        // distinct error state rather than falling through to the unscoped
        // empty state, which would mislead the user into thinking they just
        // need to impersonate a distributor.
        loadError.value = true;
        plan.value = null;
        stripeConnect.value = null;
        estimatedInvoice.value = null;
        achStatus.value = null;
      } finally {
        loading.value = false;
      }
    }

    // Refresh-on-return: when the browser comes back from Stripe Connect
    // onboarding the AccountLink return_url carries ?stripe_return=1. Re-sync
    // the live Connect flags BEFORE the normal load so the card reflects the
    // freshly-onboarded status, then strip the query param (history replace) so
    // a manual refresh doesn't re-trigger the sync.
    async function handleStripeReturn() {
      let isReturn = false;
      try {
        const params = new URLSearchParams(window.location.search);
        isReturn = params.get("stripe_return") === "1";
      } catch (e) {
        isReturn = false;
      }
      if (!isReturn) return;

      try {
        await refreshStripeConnectStatus();
      } catch (e) {
        // A failed sync must not block the page from loading; the normal
        // GET /stripe-connect below still reflects DB state, and the card
        // surfaces an error/pending badge.
      }

      // Strip ?stripe_return=1 so a refresh doesn't re-run the sync.
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete("stripe_return");
        window.history.replaceState(
          window.history.state,
          "",
          url.pathname + url.search + url.hash
        );
      } catch (e) {
        // Best-effort param strip; not fatal if the History API is unavailable.
      }
    }

    // Return leg of the EXISTING-account (OAuth) path. Stripe redirects here
    // with ?code=&state= on success, or ?error=&state= when the distributor
    // cancels. Both go to the backend: it owns state validation and the code
    // exchange, and it decides whether the returned account is the intended
    // one. The frontend never touches Stripe directly here.
    async function handleOAuthReturn() {
      let code = null;
      let state = null;
      let denial = null;
      try {
        const params = new URLSearchParams(window.location.search);
        code = params.get("code");
        state = params.get("state");
        denial = params.get("error");
      } catch (e) {
        return;
      }
      // A state with either a code or a denial is a Stripe return. Requiring
      // the code alone would silently drop the cancellation case, which is
      // an ordinary outcome the distributor still needs to see.
      if (!state || (!code && !denial)) return;

      try {
        const res = await completeStripeConnectOAuth(code, state);
        oauthResult.value = res && res.data ? res.data : null;
      } catch (e) {
        // A failed handshake must not take the page down -- the distributor
        // still needs their plan and a way to try again. The card's own
        // status badge reflects the (unchanged) DB state.
        oauthResult.value = null;
      }

      // ``state`` is SINGLE-USE. Leaving it in the URL means a refresh
      // re-posts a burned token and surfaces a spurious "link no longer
      // valid" right after a successful connection.
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete("code");
        url.searchParams.delete("state");
        url.searchParams.delete("error");
        url.searchParams.delete("scope");
        window.history.replaceState(
          window.history.state,
          "",
          url.pathname + url.search + url.hash
        );
      } catch (e) {
        // Best-effort param strip; not fatal if the History API is absent.
      }
    }

    // Re-read ONLY the ACH status (no page-level loading flicker), after the
    // bank dialog records a mandate or reports a pending verification. Same
    // swallow-on-failure rule as load(): a failed read leaves what we have.
    //
    // The page-local pending flag must not outlive a definite server answer:
    // failed and active always clear it. not_set_up clears it only when the
    // caller says the read does not immediately follow a pending dialog.
    // The server now reports pending_verification itself, so not_set_up after
    // a pending dialog is only a stale read; the flag stays until a definite
    // answer.
    function applyAchStatus(data, { notSetUpClears }) {
      achStatus.value = data;
      const st = data ? data.status : null;
      if (st === "failed" || st === "active" || (notSetUpClears && st === "not_set_up")) {
        achVerificationPending.value = false;
      }
    }

    async function refreshAchStatus({ notSetUpClears = true } = {}) {
      try {
        const achRes = await getAchStatus();
        applyAchStatus(achRes ? achRes.data : null, { notSetUpClears });
      } catch (e) {
        // Keep the previous value; the dialog already told the user what
        // happened.
      }
    }

    // The publishable key the bank form must use: the platform key on the
    // plan payload (null falls back to the build-time key in the dialog).
    const achPublishableKey = computed(
      () => (plan.value && plan.value.stripe_publishable_key) || null
    );

    // The card reports that the Connect relationship CHANGED -- an accepted
    // sibling account, or a disconnect. Both mean the callback outcome we are
    // still holding has been acted on and is now history.
    //
    // CLEARING IT HERE IS THE FIX FOR A PRODUCTION BUG (2026-09-24). This
    // handler used to be `load` directly, and `oauthResult` was set once in
    // handleOAuthReturn and never reset. The card mirrors the prop into local
    // state in data() -- it has to, because a child cannot clear its parent's
    // prop -- and load() raises `loading`, which the card sits behind via
    // v-if/v-else. So accepting a sibling account went:
    //
    //   card clears its local copy -> emit -> load() -> loading = true
    //     -> card UNMOUNTS -> load() resolves -> loading = false
    //     -> card REMOUNTS -> data() re-seeds from the STALE prop
    //
    // and the amber "Stop and check this account" panel came back over an
    // account that had just connected successfully. A manual refresh "fixed"
    // it only because a fresh page load has no callback in the URL to seed
    // from. The card's own clear was correct and is untouched; what was
    // missing is that the state's OWNER never retired it.
    //
    // Ordered before load() deliberately: load() is what triggers the
    // remount, so the prop must already be null when the card comes back.
    async function onConnectCompleted() {
      oauthResult.value = null;
      await load();
    }

    onMounted(async () => {
      await handleStripeReturn();
      await handleOAuthReturn();
      await load();
    });

    return {
      plan,
      stripeConnect,
      oauthResult,
      estimatedInvoice,
      achStatus,
      achVerificationPending,
      achPublishableKey,
      refreshAchStatus,
      loading,
      loadError,
      isUnscoped,
      hasNoSubscription,
      invoicesGoTo,
      distributorIdValue,
      editDialogSubscription,
      load,
      onConnectCompleted,
    };
  },

  methods: {
    openEditPaymentDialog() {
      this.$refs.editPaymentDialog.show();
    },
    openCancelDialog() {
      this.$refs.cancelDialog.show();
    },
    async onPaymentMethodUpdated() {
      // Re-pull the plan so the payment-method card reflects the new card.
      await this.load();
    },
    async onCancelSubscription() {
      // The dialog is a dumb confirm dialog -- the PAGE owns the new-endpoint
      // call. POST the cancel onto distributor_subscription via billingService,
      // then refresh so the page reflects the new 'cancelled' state. (Keeping
      // the call here, not in the shared dialog, is what lets the two legacy
      // SuperAdmin surfaces keep their own legacy cancel handler.)
      try {
        await cancelSubscription();
        if (this.$store && this.$store.dispatch) {
          this.$store.dispatch(
            "raiseSuccess",
            "Your subscription has been cancelled."
          );
        }
      } catch (e) {
        if (this.$store && this.$store.dispatch) {
          this.$store.dispatch("raiseError", e);
        }
      } finally {
        await this.load();
      }
    },
    onAchSetupStarted(setupData) {
      // The card created a us_bank_account SetupIntent; open the bank form
      // (Stripe's Payment Element) on it.
      this.$refs.achBankDialog.show(setupData);
    },
    async onAchCompleted() {
      // Mandate recorded: nothing is pending any more, show the new bank.
      this.achVerificationPending = false;
      await this.refreshAchStatus();
    },
    async onAchPending() {
      // Micro-deposit verification is running. Show it on the card now; the
      // server may also report it (pending_verification) on the re-read.
      this.achVerificationPending = true;
      await this.refreshAchStatus({ notSetUpClears: false });
    },
  },
};
</script>

<style scoped>
.md-header {
  font-size: var(--tf-size-h2, 1.5rem);
  font-weight: 600;
}
.md-lede {
  font-size: var(--tf-size-body, 1rem);
  color: var(--tf-fg-3, #56636e);
  margin-top: 4px;
}
.tf-empty-state {
  padding: var(--tf-sp-4, 16px);
}
.danger-zone {
  border: 1px solid var(--tf-error, var(--tf-red));
}
.tf-btn {
  border: none;
  border-radius: var(--tf-radius-sm, 8px);
  padding: var(--tf-sp-2, 8px) var(--tf-sp-5, 20px);
  font-size: var(--tf-size-body, 1rem);
  cursor: pointer;
}
.tf-btn:disabled {
  opacity: 0.6;
  cursor: default;
}
.tf-btn--danger {
  background: var(--tf-error, var(--tf-red));
  color: var(--tf-fg-on-primary, var(--tf-bg));
}
</style>
